import { E2EPage, newE2EPage } from "@stencil/core/testing";

describe("ux-input-field-number", () => {
    describe("Basic functionality", () => {
        it("should match to snapshot", async () => {
            const page = await newE2EPage();
            await page.setContent(
                `<ux-input-field-number label="test"></ux-input-field-number>`
            );
            const el = await page.find("ux-input-field");
            expect(el).toMatchSnapshot();
        });

        it("allows entry of numeric characters", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number></ux-input-field-number>`,
            });

            const number = 2;
            const numberAsString = "2";

            const component = await page.find("ux-input-field-number");
            const changeEvent = await component.spyOnEvent("uxChange");

            const input = await page.find("ux-input-field-number input");
            await input.press(numberAsString);

            const value = await component.getProperty("value");
            expect(value).toEqual(number);
            expect(changeEvent).toHaveReceivedEventTimes(1);
        });
    });

    describe("Keyboard interactions", () => {
        const NON_NUMERIC_LETTERS = stringToCharArray(
            "abcdefghijklmnopqrstuvwxyz"
        );
        const NON_NUMERIC_SYMBOLS = stringToCharArray("`=[]\\;',/");
        const NUMERIC_DIGITS = stringToCharArray("0123456789");

        it("increments/decrements with arrow up/down keys", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number value="1"></ux-input-field-number>`,
            });

            const component = await page.find("ux-input-field-number");
            const inputEl = await page.find("ux-input-field-number input");

            await inputEl.press("ArrowUp");
            expect(await component.getProperty("value")).toEqual(2);

            await inputEl.press("ArrowDown");
            await page.waitForChanges();

            expect(await component.getProperty("value")).toEqual(1);
        });
        it("disables non-numeric keys", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number locale="en-US" value="1"></ux-input-field-number>`,
            });

            await runTextInputSuite(page, NON_NUMERIC_LETTERS, false, true);
            await runTextInputSuite(page, NON_NUMERIC_SYMBOLS, false, true);
            await runTextInputSuite(page, NUMERIC_DIGITS, false, false);
        });
    });

    describe("Localization", () => {
        it("accepts a number in a different locale (de-DE)", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number locale="de-DE"></ux-input-field-number>`,
            });

            const number = 2000.5;
            const component = await page.find("ux-input-field-number");
            const inputEl = await page.find("ux-input-field-number input");

            inputEl.press("2");
            inputEl.press("0");
            inputEl.press("0");
            inputEl.press("0");
            inputEl.press(",");
            inputEl.press("5");

            await page.waitForChanges();

            expect(await component.getProperty("value")).toEqual(number);
        });

        it("shows a number in a different locale (de-DE)", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number locale="de-DE" precision="1"></ux-input-field-number>`,
            });

            const number = 2000.5;
            const numberAsLocalizedString = "2.000,5";

            const component = await page.find("ux-input-field-number");
            const inputEl = await page.find("ux-input-field-number input");

            component.setProperty("value", number);

            await page.waitForChanges();

            expect(await inputEl.getProperty("value")).toEqual(
                numberAsLocalizedString
            );
        });
    });

    describe("Validation", () => {
        it("should emit uxInvalid when calling validate method", async () => {
            const page = await newE2EPage({
                html: `<ux-input-field-number value="0" min="1" max="10"></ux-input-field-number>`,
            });

            const component = await page.find("ux-input-field-number");
            const uxInvalid = await component.spyOnEvent("uxInvalid");

            component.callMethod("validate");

            await page.waitForChanges();

            expect(await component.getProperty("error")).toEqual(true);
            expect(uxInvalid).toHaveReceivedEvent();
        });
    });
});

function stringToCharArray(str: string) {
    return str == null ? [] : str.split("");
}

async function runTextInputSuite(
    page: E2EPage,
    characters: string[],
    shift = false,
    expectDefaultPrevented = true
) {
    const input = await page.find("ux-input-field-number input");
    const keypress = await page.spyOnEvent("keypress");

    if (shift) {
        await page.keyboard.down("Shift");
    }

    for (const key of characters) {
        await input.press(key);
        const event = keypress.lastEvent;
        const valueToCheck =
            expectDefaultPrevented === true
                ? event.defaultPrevented
                : !event.defaultPrevented;

        expect(valueToCheck).toBeTruthy();
    }

    if (shift) {
        await page.keyboard.up("Shift");
    }
}
